<template>
  <div class="payment-container">
    <ion-button
      :disabled="paymentStarted"
      @click="startPayment"
      @close="paymentStarted = false"
      expand="block"
      class="meteor-btn"
    >
      <img src="~@/assets/images/meteologo.png" alt="MeteorPayment" class="meteor-logo" />
    </ion-button>

    <!-- Status-Text -->
    <div class="status" v-if="paymentStarted && !finished">
      {{ $t('paymentStarted')}}
    </div>

    <div class="status success" v-if="finished && success">
      {{ $t('paymentSuccess')}}
    </div>

    <div class="status error" v-if="finished && !success">
      {{ $t('paymentError')}}
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { IonButton } from "@ionic/vue";
import { useMutation } from '@vue/apollo-composable'
import { isPlatform } from '@ionic/vue';
import prepareCheckoutMutationFile from '../../graphql/prepareCheckout.mutation.gql'
import deleteCheckoutMutationFile from '../../graphql/deleteCheckout.mutation.gql'
import { InAppBrowser, DefaultSystemBrowserOptions } from '@capacitor/inappbrowser'

const props = defineProps({
  amount: {
    type: String,
    required: true
  }
});

const { mutate: prepareCheckoutMutation } = useMutation(prepareCheckoutMutationFile, { fetchPolicy: 'no-cache' });
const { mutate: deleteCheckoutMutation } = useMutation(deleteCheckoutMutationFile, { fetchPolicy: 'no-cache' });

const emit = defineEmits(["payment-success", "payment-failed"]);

const paymentStarted = ref(false);
const finished = ref(false);
const success = ref(false);
const checkoutId = ref('');
let popupRef = null;
let checker = ref(null)

// ------------------------------
// postMessage Listener
// ------------------------------
async function handleMessage(event) {
  if (checker.value) clearInterval(checker.value);

  if (event.origin !== window.location.origin) return;
  const msg = event.data;

  if (!msg || msg.type !== "PAYMENT_DONE") return;

  finished.value = true;
  success.value = !!msg.success;

  if (msg.success) emit("payment-success", msg);
  else {
    await deleteCheckoutMutation({ id: checkoutId.value });
    emit("payment-failed", msg);
  }
}

onMounted(() => window.addEventListener("message", handleMessage));
onUnmounted(() => {
  window.removeEventListener("message", handleMessage);
  if (popupRef && !popupRef.closed) popupRef.close();
});

// ------------------------------
// Zahlung starten
// ------------------------------
async function startPayment() {
  paymentStarted.value = true;
  success.value = false;
  finished.value = false;

  try {
    const res = await prepareCheckoutMutation({ amount: Number(props.amount).toFixed(2) });

    if (!res.data.prepareCheckout.id) throw new Error("No checkoutId returned");
    
    checkoutId.value = res.data.prepareCheckout.id;

    const url = `${window.location.origin}/hyperpay-popup?checkoutId=${encodeURIComponent(res.data.prepareCheckout.id)}`;

    if (isPlatform('capacitor')) {
      // Capacitor InAppBrowser nutzen
      popupRef = await InAppBrowser.openInWebView({
        url: `${httpLink.replace('backend-', '').replace(':4000', ':8101')}hyperpay-popup?checkoutId=${encodeURIComponent(res.data.prepareCheckout.id)}`,
        //url: `http://192.168.5.190:8101/hyperpay-popup?checkoutId=${encodeURIComponent(res.data.prepareCheckout.id)}`,
        options: DefaultSystemBrowserOptions
      });

      InAppBrowser.addListener('browserClosed', async () => {
        paymentStarted.value = false;
        finished.value = true;
        success.value = false;
        await deleteCheckoutMutation({ id: checkoutId.value });
        emit("payment-failed", { reason: "popup-closed" });

      });

      InAppBrowser.addListener("browserPageNavigationCompleted", async (event) => {
        const url = event.url;

        if (url.includes("payment?value=success")) {
          paymentStarted.value = false;
          finished.value = true;
          success.value = true;
          InAppBrowser.removeAllListeners()
          InAppBrowser.close();
          emit("payment-success", { id: checkoutId.value, reason: "payment-success" });
        }
        else if (url.includes("payment?value=failed")) {
          paymentStarted.value = false;
          finished.value = true;
          success.value = false;
          InAppBrowser.removeAllListeners()
          InAppBrowser.close();
          await deleteCheckoutMutation({ id: checkoutId.value });
          emit("payment-failed", { id: checkoutId.value, reason: "payment-failed" });
        }
        else if (url.includes("payment?value=canceled")) {
          paymentStarted.value = false;
          finished.value = true;
          success.value = false;
          InAppBrowser.removeAllListeners()
          InAppBrowser.close();
          await deleteCheckoutMutation({ id: checkoutId.value });
          emit("payment-failed", { id: checkoutId.value, reason: "popup-closed" });
        }

      });
    } else {
      // Normales Popup-Fenster
      popupRef = window.open(
            url,
            "hyperpayPayment",
            `popup=yes,
            scrollbars=no,
            toolbar=no,
            menubar=no,
            status=no,
            resizable=no`
          );    
          checker.value = setInterval(async () => {
            if (popupRef.closed) {
              clearInterval(checker.value);
              paymentStarted.value = false;
              finished.value = true;
              await deleteCheckoutMutation({ id: checkoutId.value });
              emit("payment-failed", { reason: "popup-closed" });
            }
          }, 300);
    }
    /*await Browser.open({
      url,
      presentationStyle: "popover", // oder "fullscreen"
      toolbarColor: "#ffffff",
    });*/

    /*popupRef = window.open(
      url,
      "hyperpayPayment",
      `popup=yes,
      width=${width},
      height=${height},
      top=${top},
      left=${left},
      scrollbars=no,
      toolbar=no,
      menubar=no,
      status=no,
      resizable=no`
    );

    if (!popupRef) {
      alert("Popup wurde blockiert!");
      paymentStarted.value = false;
    }*/

  } catch (err) {
    console.error("Payment failed to start:", err);
    paymentStarted.value = false;
    emit("payment-failed", err)
  }
}
</script>

<style>
.payment-container {
  text-align: center;
  margin: 10px 0;
}

/* METEORPAYMENT Style */
.meteor-btn {
  --background: linear-gradient(
    90deg,
    #B6CF6C 0%,
    #B6CF6C 18%,
    #8FBC3F 18%,
    #8FBC3F 100%
  );
  --border-radius: 999px;
  --padding-start: 12px;
  --padding-end: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.meteor-btn .meteor-logo {
  height: 34px;
  display: block;
}

/* Status-Text */
.status {
  margin-top: 12px;
  font-size: 15px;
  color: #444;
}

.status.success {
  color: #2b8a3e;
  font-weight: bold;
}

.status.error {
  color: #c92a2a;
  font-weight: bold;
}
</style>
